import path from "path";
import { call, parallel, run, safe } from "starfx";

import { objOf } from "../helpers";
import { callValue } from "../helpers/callValue";
import { callReturn, safeLast } from "../helpers/fxhelpers";
import { checkFileExists as checkFileExistsApi } from "../xThunks/n-file-sys-opts";

import type { Result } from "starfx";
import type { Operation } from "starfx";
import type { TKarmaFs } from "@app/types/insider";
export function decodeAttributes(attr) {
  const flags = [];
  if (attr & 0x10) flags.push("Directory");
  if (attr & 0x20) flags.push("Archive");
  if (attr & 0x01) flags.push("Read-only");
  if (attr & 0x02) flags.push("Hidden");
  if (attr & 0x04) flags.push("System");
  return flags.join(", ");
}

export function extractFileDirectory(fileName) {
  const lastSlashIndex = fileName.lastIndexOf("/");
  const lastBackslashIndex = fileName.lastIndexOf("\\");
  const separatorIndex = Math.max(lastSlashIndex, lastBackslashIndex);

  if (separatorIndex !== -1) {
    return fileName.substring(0, separatorIndex);
  }
  return "";
}

export function extractFileName(filePath) {
  const lastSlashIndex = filePath.lastIndexOf("/");
  const lastBackslashIndex = filePath.lastIndexOf("\\");
  const separatorIndex = Math.max(lastSlashIndex, lastBackslashIndex);

  if (separatorIndex !== -1) {
    return filePath.substring(separatorIndex + 1); // Add 1 to exclude the separator itself
  }

  return filePath;
}

function normalizeWindowsPath(issFilePath) {
  const parts = issFilePath.split("\\");
  const resolvedParts = [];
  for (const part of parts) {
    if (part === "..") {
      // Remove the previous directory if '..' is encountered
      resolvedParts.pop();
    } else {
      resolvedParts.push(part);
    }
  }
  return resolvedParts.join("\\");
}

export function resolvePaths(referenceFile, includedFile) {
  const referenceDirectory = extractFileDirectory(referenceFile);
  // Replace any forward slashes with backslashes
  const normalizedIncluded = includedFile.replace(/\//g, "\\");
  const combinedPath = referenceDirectory + "\\" + normalizedIncluded;
  return normalizeWindowsPath(combinedPath);
}

export const loadFileContent = function* (
  fileName: string
): Operation<string | null> {
  return yield* run(function* (): Operation<string> {
    const delphiFSModules = yield* safe(() =>
      window?.delphiRequire("fs")
    ) as Operation<Result<TKarmaFs>>;
    const fs = callValue(
      delphiFSModules,
      "Cannot load the file system module. Aborting.",
      "terminal"
    );
    const getFileContent = yield* safe(() => fs.readFile(fileName, "r"));
    if ("error" in getFileContent) {
      return null;
    }
    if ("value" in getFileContent) {
      return getFileContent.value;
    }
  });
};

export const checkFileExists = function* (
  fileName: string
): Operation<boolean> {
  if (process.env.REACT_APP_MODE === "REACT") {
    const res = yield* call(checkFileExistsApi(fileName));
    return res;
  }

  // console.log("fileName", fileName);
  return yield* run(function* (): Operation<boolean> {
    const delphiFSModules = yield* safe(() =>
      window.delphiRequire("fs")
    ) as Operation<Result<TKarmaFs>>;
    const fs = callValue(
      delphiFSModules,
      "Cannot load the file system module. Aborting.",
      "terminal"
    );
    const fileExists = yield* safe(() => fs.fileExists(fileName));
    // console.log('fileExists', fileExists)
    if ("error" in fileExists) {
      return false;
    }
    if ("value" in fileExists) {
      return fileExists.value;
    }
    return true;
  });
};

export const getDirectoryFiles = function* (
  directory: string
): Operation<string[]> {
  const delphiFSModules = yield* safe(() =>
    window.delphiRequire("fs")
  ) as Operation<Result<TKarmaFs>>;
  const fs = callValue(
    delphiFSModules,
    "Cannot load the file system module. Aborting.",
    "terminal"
  );
  const result = yield* safe(() => fs.readdir(directory));
  if ("error" in result) {
    return [];
  }
  if ("value" in result) {
    return result.value;
  }
};

export const writeFile = function* (fileName: string, content: string) {
  const delphiFSModules = yield* safe(() =>
    window.delphiRequire("fs")
  ) as Operation<Result<TKarmaFs>>;
  const fs = callValue(
    delphiFSModules,
    "Cannot load the file system module. Aborting.",
    "terminal"
  );
  const result = yield* safe(() => fs.writeFile(fileName, content, "w"));
  if ("error" in result) {
    return result.error.message;
  }
  if ("value" in result) {
    return result.value;
  }
};

export const copyFile = function* (source: string, target: string) {
  const delphiFSModules = yield* safe(() =>
    window.delphiRequire("fs")
  ) as Operation<Result<TKarmaFs>>;
  const fs = callValue(
    delphiFSModules,
    "Cannot load the file system module. Aborting.",
    "terminal"
  );
  const result = yield* safe(() => fs.copyFile(source, target));
  return result;
};

/* 
I searched too many times for this, so I'm putting it here for future reference.
#expr SaveToFile(AddBackslash(SourcePath) + "Preprocessed.iss")
https://stackoverflow.com/questions/48914783/how-to-use-inno-setup-preprocessor-directive-in-code-section

*/

export const deleteFile = function* (fileName: string) {
  const delphiFSModules = yield* safe(() =>
    window.delphiRequire("fs")
  ) as Operation<Result<TKarmaFs>>;
  const fs = callValue(
    delphiFSModules,
    "Cannot load the file system module. Aborting.",
    "terminal"
  );
  const result = yield* safe(() => fs.rm(fileName, false));
  return result;
};

export const deleteDirectory = function* (directory: string) {
  const delphiFSModules = yield* safe(() =>
    window.delphiRequire("fs")
  ) as Operation<Result<TKarmaFs>>;
  const fs = callValue(
    delphiFSModules,
    "Cannot load the file system module. Aborting.",
    "terminal"
  );
  const result = yield* safe(() => fs.rm(directory, true));
  return result;
};
